import { MAX_SPEED } from "./sim";
import { MapDef, Track } from "./tracks";
import { TUNING } from "./tuning";

// What a lap asks of a driver, read off the track's geometry: its corners,
// how long each takes at top speed and which drift tier it has room for, its
// straights, and everything on the road in order. For the design lint in
// sim.test.ts, and printed with KART_TRACK_REPORT=1 (TRACK-IMPROVEMENTS.md,
// TOOL-1).

/** Tighter than this radius, the road counts as turning. */
const CORNER_RADIUS = 60;
/** Wider than this on average, a corner is taken flat out: a drift turns tighter than the road does. */
const FLAT_RADIUS = 45;
/**
 * The least the kart turns by the time each tier is charged, in degrees,
 * counting the full lock that starts the drift: a drift can't be steered out
 * of, so even its loosest arc turns this far (sim.test.ts measures it).
 */
export const TIER_TURN = [33, 65, 105];
/** Seconds a corner must last for each tier: holding to start the drift, and charging it. */
export const tierTime = (tier: number) => TUNING.drift.enterHold + TUNING.drift.tiers[tier - 1].charge;
/** Roughly what a tier's boost is worth, in seconds gained: how much faster, for how long. */
export const tierGain = (tier: number) => (tier ? (TUNING.drift.tiers[tier - 1].mul - 1) * TUNING.drift.tiers[tier - 1].time : 0);

export interface Corner {
  /** The sample it starts at, and how many samples it runs. */
  from: number;
  len: number;
  /** Degrees, signed: positive turns to the road's right. */
  turn: number;
  minR: number;
  avgR: number;
  /** At top speed. */
  secs: number;
  /** The best drift tier it has room for, 0 for none. */
  tier: number;
}

export function corners(track: Track): Corner[] {
  const { samples, n } = track;
  const heading = samples.map((s) => Math.atan2(s.tz, s.tx));
  const d = heading.map((h, i) => {
    const x = heading[(i + 1) % n] - h;
    return Math.atan2(Math.sin(x), Math.cos(x));
  });
  // Curvature, smoothed over 9 samples so the resampling's jitter isn't a corner.
  const k = d.map((_, i) => {
    let sum = 0;
    for (let j = -4; j <= 4; j++) sum += d[(i + j + n) % n];
    return sum / 9;
  });
  const turning = (i: number) => Math.abs(k[i % n]) > 1 / CORNER_RADIUS;
  // Start on a straight, so no corner is cut in two by the wrap.
  const start = Math.max(0, k.findIndex((_, i) => !turning(i)));
  const out: Corner[] = [];
  let from = -1;
  for (let q = 0; q <= n; q++) {
    const on = q < n && turning(start + q);
    if (on && from < 0) from = q;
    if (on || from < 0) continue;
    let turn = 0;
    let minR = Infinity;
    for (let j = start + from; j < start + q; j++) {
      turn += d[j % n];
      minR = Math.min(minR, 1 / Math.abs(k[j % n]));
    }
    const len = q - from;
    const deg = (Math.abs(turn) * 180) / Math.PI;
    const avgR = len / Math.abs(turn);
    const secs = len / MAX_SPEED;
    const tier = avgR > FLAT_RADIUS ? 0 : TIER_TURN.filter((at, t) => deg >= at && secs >= tierTime(t + 1)).length;
    out.push({ from: (start + from) % n, len, turn: Math.round((turn * 180) / Math.PI), minR: Math.round(minR), avgR: Math.round(avgR), secs, tier });
    from = -1;
  }
  return out.sort((a, b) => a.from - b.from);
}

/** Whether sample `idx` is inside a corner there's a drift in, rather than a flat-out kink. */
export const inDriftCorner = (list: Corner[], n: number, idx: number, minTier = 1) =>
  list.some((c) => c.tier >= minTier && (idx - c.from + n) % n < c.len);

/**
 * What a shortcut saves, in seconds, taken without a boost: the road's length
 * at top speed less the drift boosts of the corners it skips, against its own
 * length at its speed.
 */
export function shortcutSaving(track: Track, list: Corner[], cut: Track["cuts"][number], drag = cut.speed) {
  const { n } = track;
  const skipped = list.filter((c) => {
    const at = (c.from - cut.a + n) % n;
    return at + c.len <= cut.skip;
  });
  const road = cut.skip / MAX_SPEED - skipped.reduce((sum, c) => sum + tierGain(c.tier), 0);
  return road - cut.len / (MAX_SPEED * drag);
}

/**
 * What the same shortcut saves with an energy drink (or Jägermeister) fired at
 * its gate: the boost's seconds cover more of the path, at its higher speed.
 */
export function shortcutSavingBoosted(track: Track, list: Corner[], cut: Track["cuts"][number]) {
  const { time, mul } = TUNING.boost.item;
  const speed = MAX_SPEED * cut.speed;
  const boosted = Math.min(cut.len, speed * mul * time);
  return shortcutSaving(track, list, cut) + boosted * (1 / speed - 1 / (speed * mul));
}

/** The report, as text: the lap, its corners and straights, and everything on it in order. */
export function trackReport(map: MapDef, track: Track) {
  const { n } = track;
  const list = corners(track);
  const pct = (idx: number) => (idx / n).toFixed(3);
  const lines = [`${map.name} (${map.key}${map.variant ? `, ${map.variant}` : ""}): ${n} u, ${(n / MAX_SPEED).toFixed(1)} s flat out`];
  lines.push(`  corners (${list.length}), tiers ${[0, 1, 2, 3].map((t) => `${t}:${list.filter((c) => c.tier === t).length}`).join(" ")}`);
  for (const c of list) {
    lines.push(`    ${pct(c.from)} ${String(c.turn).padStart(5)}°  ${c.len} u ${c.secs.toFixed(2)} s  radius min ${c.minR} avg ${c.avgR}  tier ${c.tier || "flat"}`);
  }
  // Straights: the stretches between corners.
  const gaps = list.map((c, i) => {
    const next = list[(i + 1) % list.length];
    return { from: (c.from + c.len) % n, len: (next.from - c.from - c.len + n) % n };
  });
  const longest = gaps.reduce((a, b) => (b.len > a.len ? b : a), { from: 0, len: 0 });
  lines.push(`  longest straight ${pct(longest.from)}, ${longest.len} u (${(longest.len / MAX_SPEED).toFixed(1)} s)`);
  track.cuts.forEach((cut) => {
    lines.push(`  shortcut ${pct(cut.a)}: skips ${cut.skip} u for ${Math.round(cut.len)} u, at ${cut.speed} speed saves ${shortcutSaving(track, list, cut).toFixed(2)} s without a boost, ${shortcutSavingBoosted(track, list, cut).toFixed(2)} s with an energy drink`);
  });
  const things: [number, string][] = [
    ...track.pads.map((p): [number, string] => [p.idx, `pad ${p.lat}`]),
    ...map.boxes.map((f): [number, string] => [Math.floor(f * n), "boxes"]),
    ...track.ramps.map((r): [number, string] => [r.idx, `ramp ${r.lat}`]),
    ...track.branches.map((b): [number, string] => [b.idx, `branch ${b.lat}`]),
    ...track.kubbs.map((k): [number, string] => [k.idx, k.king ? "kubb king" : "kubb"]),
    ...track.snowmen.map((s): [number, string] => [s.idx, `snowman ${s.lat}`]),
    ...track.hazards.map((h): [number, string] => [h.idx, `${h.kind} ${h.lat}`]),
    ...track.ducks.map((i): [number, string] => [i, "ducks"]),
    ...track.skiers.map((i): [number, string] => [i, "skier"]),
    ...track.ice.map(([from, len]): [number, string] => [from, `ice ${len} u`]),
    ...track.cuts.map((c): [number, string] => [c.a, `shortcut ${c.skip} u`]),
  ].sort((a, b) => a[0] - b[0]);
  lines.push(`  on the road (${things.length}):`);
  for (const [idx, what] of things) lines.push(`    ${pct(idx)} ${(idx / MAX_SPEED).toFixed(1)} s  ${what}${inDriftCorner(list, n, idx) ? "  (in a corner)" : ""}`);
  return lines.join("\n");
}

